import React, { useState, useEffect, createContext } from "react";
import { DevicesStepResponse } from "../components/TrustedDevices/TrustedDevice.type";
import useConfig from "../hooks/useConfig";
import { getStateCode, UsaStatesLowerCase } from "../utils/getStateCode";

export type StateContextProperties = {
  cidmUrl: string;
  stateUSACode: UsaStatesLowerCase | "";
  accessToken?: string;
  isAuthorizeError: boolean;
  languageCode: "en" | "es";
  authorizationCode?: string;
  setAuthorizationCode(_: string): void;
  setIsAuthorizeError(_: boolean): void;
  setAccessToken(_: string): void;
  setLanguageCode(_: "en" | "es"): void;
  passKeyCount: number;
  setPassKeyCount(_: number): void;
  removeDeviceSuccess: boolean;
  setRemoveDeviceSuccess: (_: boolean) => void;
  lastRemovedDeviceMessage: string;
  setLastRemovedDeviceMessage(_: string): void;
  isError: string;
  setIsError(_: string): void;
  addPassKeySuccess: boolean;
  setAddPassKeySuccess: (_: boolean) => void;
  addedPassKeyMessage: string;
  setAddedPassKeyMessage(_: string): void;
  passKeyStep2Response?: DevicesStepResponse;
  setPassKeyStep2Response: React.Dispatch<React.SetStateAction<DevicesStepResponse | undefined>>;
};

export const StateContext = createContext<StateContextProperties>({
  stateUSACode: getStateCode(),
  cidmUrl: "",
  isAuthorizeError: false,
  languageCode: "en",
  authorizationCode: "",
  setAuthorizationCode: (_: "string") => null,
  setAccessToken: () => 43,
  setIsAuthorizeError: () => 43,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setLanguageCode: (_: "en" | "es") => {},
  passKeyCount: 0,
  setPassKeyCount: () => 0,
  removeDeviceSuccess: false,
  setRemoveDeviceSuccess: () => 43,
  lastRemovedDeviceMessage: "",
  setLastRemovedDeviceMessage: (_: "string") => null,
  isError: "",
  setIsError: (_: "string") => null,
  addPassKeySuccess: false,
  setAddPassKeySuccess: () => 43,
  addedPassKeyMessage: "",
  setAddedPassKeyMessage: (_: "string") => null,
  passKeyStep2Response: undefined,
  setPassKeyStep2Response: () => {},
});

declare let window: Window;

const StateContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [accessToken, setAccessToken] = useState<string>();
  const [authorizationCode, setAuthorizationCode] = useState<string>("");
  const [isAuthorizeError, setIsAuthorizeError] = useState<boolean>(false);
  const [cidmUrl, setCidmUrl] = useState("");
  const [stateUSACode, setStateUSACode] = useState<UsaStatesLowerCase | "">(getStateCode());
  const [href, setHref] = useState("");
  const savedLanguageCode = localStorage.getItem("lang");
  const [language, setContextLanguageCode] = useState<"en" | "es">(savedLanguageCode === "es" ? "es" : "en");
  const setLanguageCode = (languageCode: "en" | "es") => {
    localStorage.setItem("lang", languageCode);
    setContextLanguageCode(languageCode);
  };
  const environmentVars = useConfig();
  const [passKeyCount, setPassKeyCount] = useState<number>(0);
  const [removeDeviceSuccess, setRemoveDeviceSuccess] = useState<boolean>(false);
  const [lastRemovedDeviceMessage, setLastRemovedDeviceMessage] = useState<string>("");
  const [isError, setIsError] = useState<string>("");
  const [addPassKeySuccess, setAddPassKeySuccess] = useState<boolean>(false);
  const [addedPassKeyMessage, setAddedPassKeyMessage] = useState<string>("");
  const [passKeyStep2Response, setPassKeyStep2Response] = useState<DevicesStepResponse>();

  useEffect(() => {
    if (typeof window !== "undefined") {
      setHref(window?.location.href);
      const stateCode = getStateCode();
      if (stateCode) {
        setStateUSACode(stateCode);
      }
    }
  }, [href]);

  useEffect(() => {
    if (stateUSACode) {
      setCidmUrl(environmentVars?.REACT_PUBLIC_HOST?.replace("bcbsil", `bcbs${stateUSACode}`));
    }
  }, [stateUSACode]);

  return (
    <StateContext.Provider
      value={{
        stateUSACode,
        cidmUrl,
        accessToken,
        languageCode: language,
        authorizationCode,
        setAuthorizationCode,
        setAccessToken,
        isAuthorizeError,
        setIsAuthorizeError,
        setLanguageCode,
        passKeyCount,
        setPassKeyCount,
        removeDeviceSuccess,
        setRemoveDeviceSuccess,
        lastRemovedDeviceMessage,
        setLastRemovedDeviceMessage,
        isError,
        setIsError,
        setAddPassKeySuccess,
        addPassKeySuccess,
        setAddedPassKeyMessage,
        addedPassKeyMessage,
        passKeyStep2Response,
        setPassKeyStep2Response,
      }}
    >
      {children}
    </StateContext.Provider>
  );
};
export default StateContextProvider;
